import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import {
  applyOptimistic,
  rollbackOptimistic,
  type OptimisticSnapshot,
} from "@/lib/api/optimistic";

import { notificationsApi } from "./api";
import type { Notification } from "./types";

const KEY = ["notifications"] as const;

export function useNotifications(page = 1) {
  return useQuery({
    queryKey: [...KEY, "list", page],
    queryFn: () => notificationsApi.list({ page }),
    staleTime: 30_000,
  });
}

export function useUnreadCount() {
  return useQuery({
    queryKey: [...KEY, "unread-count"],
    queryFn: notificationsApi.unreadCount,
    // Rafraîchit régulièrement pour faire vivre le badge de la cloche.
    refetchInterval: 60_000,
    staleTime: 30_000,
  });
}

export function useMarkNotificationRead() {
  const queryClient = useQueryClient();
  return useMutation<unknown, unknown, string, OptimisticSnapshot>({
    mutationFn: (id: string) => notificationsApi.markRead(id),
    // Ouvrir une notification doit la marquer lue instantanément : c'est le
    // geste le plus répété de la journée, et le plus sensible à la latence.
    onMutate: (id) =>
      applyOptimistic<Notification>(queryClient, KEY, id, (notification) => ({
        ...notification,
        is_read: true,
      })),
    onError: (_err, _id, snapshot) => rollbackOptimistic(queryClient, snapshot),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: KEY });
    },
  });
}

export function useMarkAllNotificationsRead() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: notificationsApi.markAllRead,
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: KEY });
    },
  });
}
